import { useEffect, useState } from "react";
import type { AdminApi } from "@/api";
import type { DetectionDatabase, DetectionOptions, DetectionPreview, DetectionSource } from "@/types/issuePipeline";
import type { LlmModelOption } from "@/types";
import { Banner, Button, Card } from "@/components/ui";
import { cn } from "@/lib/utils";
import { DETECTION_DEFAULT_MODEL_LABEL, modelLabel, SCAN_KEYS, scanModels as scanChoices, reviewModels as reviewChoices } from "@/lib/llmModels";
import { conversationHref, inputClass, pipelineError } from "./pipelineShared";

const SOURCES: Array<[DetectionSource, string]> = [["all", "전체"], ["dev", "dev"], ["prod", "prod"]];
/** Job states `detectCandidates` answers; anything else shows as the server sent it. */
const jobStatuses: Record<string, string> = { queued: "대기열 등록" };
const sampleStatuses: Record<string, string> = {
  retry: "실패·미완료 재시도",
  ready: "분석 예정",
  already_analyzed: "이미 분석함 · 건너뜀",
};
const kstTime = (value: string) => new Date(value).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" });
const databaseLabel = (db: DetectionDatabase | null) => db
  ? `${db.host || "호스트 미확인"}${db.port ? `:${db.port}` : ""} / ${db.database || "DB 미확인"}`
  : "설정 없음";

type Period = "yesterday" | "week" | "all" | "custom";
const PERIODS: Array<[Period, string]> = [
  ["yesterday", "어제"],
  ["week", "최근 7일"],
  ["all", "전체 기간"],
  ["custom", "직접 지정"],
];

/** Midnight KST `daysAgo` days before today, as UTC ISO. */
function kstMidnight(daysAgo: number, now = new Date()) {
  const kst = new Date(now.getTime() + 9 * 3600_000);
  return new Date(Date.UTC(kst.getUTCFullYear(), kst.getUTCMonth(), kst.getUTCDate() - daysAgo) - 9 * 3600_000).toISOString();
}

/**
 * Run detection by hand: pick a period and which DB, preview, run.
 *
 * Only what an operator decides is on screen. Batching, scan size and
 * concurrency are server defaults; the call cap is the one cost lever and sits
 * under 고급 설정 with how the server reads the data. Prompts and models are
 * edited in Prompt Lab, not here.
 *
 * Without prod access only dev is offered: "전체" reads prod too.
 */
export function DetectionControls({ api, prodAccess }: { api: AdminApi; prodAccess: boolean }) {
  const [source, setSource] = useState<DetectionSource>(prodAccess ? "all" : "dev");
  const [period, setPeriod] = useState<Period>("yesterday");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [callBudget, setCallBudget] = useState("100");
  const [scanModels, setScanModels] = useState<LlmModelOption[]>([]);
  const [reviewModels, setReviewModels] = useState<LlmModelOption[]>([]);
  const [scanModel, setScanModel] = useState("");
  const [reviewModel, setReviewModel] = useState("");
  const [preview, setPreview] = useState<DetectionPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!prodAccess && source !== "dev") { setSource("dev"); setPreview(null); setMessage(""); }
  }, [prodAccess, source]);

  useEffect(() => {
    let cancelled = false;
    // 1차 supports all scan transports; 1.1차 supports Gemini and the CLI/Qwen routes.
    api.listLlmModels()
      .then((json) => {
        if (cancelled) return;
        setScanModels(scanChoices(json.models));
        setReviewModels(reviewChoices(json.models));
      })
      .catch(() => { if (!cancelled) { setScanModels([]); setReviewModels([]); } });
    return () => { cancelled = true; };
  }, [api]);

  function changed() { setPreview(null); setMessage(""); setError(""); }

  function range(): Pick<DetectionOptions, "start_at" | "end_at"> {
    if (period === "yesterday") return { start_at: kstMidnight(1), end_at: kstMidnight(0) };
    if (period === "week") return { start_at: kstMidnight(6), end_at: null };
    if (period === "all") return { start_at: null, end_at: null };
    if (!start || !end) throw new Error("시작과 종료 시각을 모두 입력해 주세요.");
    const toUTC = (value: string) => new Date(`${value.length === 16 ? value + ":00" : value}+09:00`).toISOString();
    const start_at = toUTC(start), end_at = toUTC(end);
    if (start_at >= end_at) throw new Error("종료 시각은 시작 시각보다 뒤여야 합니다.");
    return { start_at, end_at };
  }

  function options(): DetectionOptions {
    const maxCalls = Number(callBudget);
    if (!Number.isInteger(maxCalls) || maxCalls < 1 || maxCalls > 10000) throw new Error("AI 호출 상한은 1~10,000회입니다.");
    return {
      source_environment: source, ...range(), max_model_calls: maxCalls,
      ...(scanModel ? { scan_model: scanModel } : {}),
      ...(reviewModel ? { review_model: reviewModel } : {}),
    };
  }

  async function inspect() {
    setError(""); setMessage(""); setPreview(null);
    try {
      const body = options();
      setBusy(true);
      setPreview(await api.previewDetection(body));
    } catch (err) { setError(pipelineError(err)); }
    finally { setBusy(false); }
  }

  async function execute() {
    if (!preview) return;
    setError(""); setBusy(true);
    try {
      const result = await api.detectCandidates(preview.scope);
      setPreview(null);
      setMessage(`탐지를 시작했습니다 · 작업 ${result.task_id} · 상태 ${jobStatuses[result.status] ?? result.status}. 끝나면 실행 이력과 후보 목록에 반영됩니다.`);
    } catch (err) { setError(pipelineError(err)); }
    finally { setBusy(false); }
  }

  const nothingToDo = preview && preview.pending_chunks === 0 && (preview.pending_reviews ?? 0) === 0 && preview.feedback_responses === 0;
  const blocked = preview?.scan_model_available === false;

  return (
    <Card className="grid gap-4 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">후보 탐지</h2>
        <a className="text-xs text-link hover:underline" href="/prompt-lab">탐지 프롬프트·모델은 Prompt Lab에서 관리 →</a>
      </div>
      <p className="text-sm text-muted-foreground">
        기간 안의 사용자 발화에서 불편 신호를 찾고, 1.1차를 통과하면 자동으로 TC를 만듭니다. 👎은 바로 TC를 만듭니다.
      </p>

      <fieldset disabled={busy} className="grid gap-3">
        <legend className="sr-only">탐지 범위</legend>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex items-center gap-2" role="group" aria-label="기간">
            <span className="text-sm text-muted-foreground">기간</span>
            <div className="flex overflow-hidden rounded-md border border-border">
              {PERIODS.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={period === value}
                  onClick={() => { changed(); setPeriod(value); }}
                  className={cn(
                    "border-r border-border px-3 py-1.5 text-sm last:border-r-0",
                    period === value ? "bg-accent text-accent-foreground" : "bg-card hover:bg-surface-subtle",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2" role="group" aria-label="대상 DB">
            <span className="text-sm text-muted-foreground">대상 DB</span>
            <div className="flex overflow-hidden rounded-md border border-border">
              {SOURCES.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={source === value}
                  disabled={!prodAccess && value !== "dev"}
                  title={!prodAccess && value !== "dev" ? "prod 권한 필요" : undefined}
                  onClick={() => { changed(); setSource(value); }}
                  className={cn(
                    "border-r border-border px-3 py-1.5 text-sm last:border-r-0",
                    source === value ? "bg-accent text-accent-foreground" : "bg-card hover:bg-surface-subtle",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
        {!prodAccess && <p className="text-xs text-muted-foreground">prod 권한이 없어 dev만 탐지할 수 있습니다.</p>}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          {([
            ["1차 신호 탐지 모델", scanModel, setScanModel, scanModels, modelLabel],
            ["1.1차 피드백 작성 모델", reviewModel, setReviewModel, reviewModels, (m: LlmModelOption) => m.label],
          ] as const).map(([label, value, set, models, name]) => (
            <label key={label} className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">{label}</span>
              <select className={inputClass} value={value} onChange={(e) => { changed(); set(e.target.value); }}>
                <option value="">{DETECTION_DEFAULT_MODEL_LABEL}</option>
                {models.map((model) => (
                  <option key={model.id} value={model.id} disabled={!model.is_enabled}>{name(model)}</option>
                ))}
              </select>
            </label>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          공란은 실행별 모델 지정을 생략합니다. 서버 기본은 Claude CLI(claude-opus-5-5) · 실패·한도·인증 실패 시 Qwen qwen3.8-27b 예비입니다.
          1차는 활성 프롬프트에 저장된 모델이 있으면 우선하며, 실제 적용 모델은 서버 설정과 미리보기에서 확인하세요.
        </p>
        {period === "custom" && (
          <div className="flex flex-wrap gap-3">
            <label className="grid gap-1 text-sm">시작 (KST · 포함)
              <input className={inputClass} type="datetime-local" value={start} onChange={(e) => { changed(); setStart(e.target.value); }} />
            </label>
            <label className="grid gap-1 text-sm">종료 (KST · 미포함)
              <input className={inputClass} type="datetime-local" value={end} onChange={(e) => { changed(); setEnd(e.target.value); }} />
            </label>
          </div>
        )}
        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground">고급 설정</summary>
          <div className="mt-2 grid gap-2">
            <label className="flex items-center gap-2">실행당 AI 호출 상한 (DB별)
              <input className={cn(inputClass, "w-28")} type="number" min={1} max={10000} value={callBudget}
                onChange={(e) => { changed(); setCallBudget(e.target.value); }} />
              <span className="text-xs text-muted-foreground">넘치면 남은 건 다음 실행에서 이어서 처리합니다.</span>
            </label>
          </div>
        </details>
      </fieldset>

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" disabled={busy} onClick={() => void inspect()}>{busy ? "확인 중…" : "미리보기"}</Button>
        <Button disabled={busy || !preview || Boolean(nothingToDo) || blocked} onClick={() => void execute()}>탐지 시작</Button>
        {preview && (
          <span className="text-sm" aria-live="polite">
            대화 <strong>{preview.eligible_conversations}개</strong> · 메시지 <strong>{preview.eligible_messages}건</strong> · 👎 <strong>{preview.feedback_responses}건</strong> · AI 호출 최대 <strong>{preview.model_calls_upper_bound}회</strong>
            {nothingToDo && <span className="text-muted-foreground"> · 새로 처리할 것이 없습니다</span>}
            <span className="block text-xs text-muted-foreground">
              1차 {preview.model}{preview.input_policy.scan_method ? ` (${preview.input_policy.scan_method})` : ""}
              {preview.review_model ? ` · 1.1차 ${preview.review_model}` : ""}
            </span>
          </span>
        )}
      </div>
      {preview?.unavailable_environments?.length ? (
        <p className="text-sm text-muted-foreground">
          {preview.unavailable_environments.join(", ")} DB가 이 서버에 연결되어 있지 않아 제외됩니다.
        </p>
      ) : null}
      {preview?.failed_environments?.map((failure) => (
        <p key={failure.source_environment} className="text-sm text-muted-foreground">
          {failure.source_environment} DB를 읽지 못해 이번 미리보기에서 빠졌습니다 ({failure.error}). 탐지를 시작하면 다시 시도합니다.
        </p>
      ))}
      {blocked && (
        <Banner tone="error">
          1차 모델 {preview?.model}을(를) 이 서버에서 실행할 수 없습니다
          {preview?.scan_model_unavailable_reason === "key_missing"
            ? ` (${SCAN_KEYS[preview.input_policy.scan_transport ?? ""] ?? "접속 키"} 없음)` : ""}.
          다른 1차 모델을 고르거나 키를 설정해 주세요. 다른 모델로 대신 실행하지 않습니다.
        </Banner>
      )}
      {error && <Banner tone="error">{error}</Banner>}
      {message && <p role="status" className="text-sm">{message}</p>}

      {preview && (
        <details className="rounded-md border border-border p-3 text-sm" aria-label="탐지 대상 미리보기">
          <summary className="cursor-pointer text-muted-foreground">
            상세 · {preview.scope.start_at ? kstTime(preview.scope.start_at) : "처음"} ~ {preview.scope.end_at ? kstTime(preview.scope.end_at) : "현재"} KST
          </summary>
          <div className="mt-3 grid gap-3 text-xs">
            <p>
              {preview.by_environment && <>{Object.entries(preview.by_environment).map(([env, count]) =>
                `${env} 대화 ${count.eligible_conversations}개 · 메시지 ${count.eligible_messages}건 · 👎 ${count.feedback_responses}건`).join(" / ")}{" · "}</>}
              이미 분석 {preview.completed_chunks}묶음 · 새로 분석 {preview.pending_chunks}묶음 · 1.1차 대기 {preview.pending_reviews ?? 0}건
            </p>
            <div className="overflow-x-auto"><table className="w-full text-left">
              <thead><tr className="text-muted-foreground"><th className="p-2">대화 · 시작 (KST)</th><th className="p-2">첫 내용</th><th className="p-2">상태</th></tr></thead>
              <tbody>{preview.samples.map((sample, index) => <tr key={`${sample.message_id}:${index}`} className="border-t border-border align-top">
                <td className="p-2"><a className="text-link hover:underline" href={conversationHref(sample.source_environment ?? preview.source_environment, sample.session_id, sample.message_id)}>{sample.source_environment ?? preview.source_environment} 대화 ↗</a><p className="text-muted-foreground">{kstTime(sample.started_at)}</p></td>
                <td className="max-w-96 p-2 whitespace-pre-wrap">{sample.preview}</td>
                <td className="p-2 whitespace-nowrap">{sampleStatuses[sample.status] || sample.status}</td>
              </tr>)}</tbody>
            </table></div>
            <p className="text-muted-foreground">
              조회 DB {preview.by_environment
                ? Object.entries(preview.by_environment).map(([env, count]) => `${env} ${databaseLabel(count.retrieval.connected_database)}`).join(" · ")
                : databaseLabel(preview.retrieval.connected_database)} · 프롬프트 {preview.prompt_version}
            </p>
          </div>
        </details>
      )}
    </Card>
  );
}
